import React, { useState, useEffect, useRef } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Avatar,
  Textarea, Divider, Tabs, Tab, TabList, TabPanels, TabPanel,
  useColorModeValue,
} from "@chakra-ui/react";
import { Send, X, RefreshCw, ArchiveRestore } from "lucide-react";
import Card from "components/card/Card.js";
import BeatLoader from "react-spinners/BeatLoader";
import { QuickAlert } from "theme/components/alerts";
import { userController } from "lib/user";
import { supportChatStatusLabels, labelFor } from "lib/labels";

export default function Support() {
  const { getSingleRecord, saveRecord } = userController();
  const { setAlert, clearAlert, showAlert } = QuickAlert();

  const [chats, setChats]               = useState([]);
  const [archive, setArchive]           = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages]         = useState([]);
  const [replyText, setReplyText]       = useState("");
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [loadingMsgs, setLoadingMsgs]   = useState(false);
  const [sending, setSending]           = useState(false);
  const [closing, setClosing]           = useState(false);
  const [reopening, setReopening]       = useState(false);
  const [activeTab, setActiveTab]       = useState(0);

  const messagesEndRef = useRef(null);
  const pollChatsRef   = useRef(null);
  const pollMsgsRef    = useRef(null);

  const cardBg       = useColorModeValue("white", "navy.800");
  const borderClr    = useColorModeValue("gray.200", "whiteAlpha.100");
  const inboundBg    = useColorModeValue("gray.100", "whiteAlpha.100");
  const selectedBg   = useColorModeValue("brand.50", "whiteAlpha.200");
  const hoverBg      = useColorModeValue("gray.50", "whiteAlpha.100");
  const tabInactiveBg    = useColorModeValue("gray.100", "whiteAlpha.100");
  const tabInactiveHover = useColorModeValue("gray.200", "whiteAlpha.200");

  /*
  |--------------------------------------------------------------------------
  | Fetch helpers
  |--------------------------------------------------------------------------
  */
  const fetchChats = () => {
    getSingleRecord("/user/support/chats")
      .then((res) => { setChats(res.data.data || []); setLoadingChats(false); })
      .catch(() => setLoadingChats(false));
  };

  const fetchArchive = () => {
    setLoadingArchive(true);
    getSingleRecord("/user/support/chats?archive=1")
      .then((res) => { setArchive(res.data.data || []); setLoadingArchive(false); })
      .catch(() => setLoadingArchive(false));
  };

  const fetchMessages = (chatId) => {
    getSingleRecord(`/user/support/chats/${chatId}/messages`).then((res) => {
      setMessages(res.data.data || []);
      setLoadingMsgs(false);
      setChats((prev) => prev.map((c) => c.id === chatId ? { ...c, unread_count: 0 } : c));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }).catch(() => setLoadingMsgs(false));
  };

  /*
  |--------------------------------------------------------------------------
  | Actions
  |--------------------------------------------------------------------------
  */
  const openChat = (chat) => {
    setSelectedChat(chat);
    setMessages([]);
    setLoadingMsgs(true);
    clearAlert();
    fetchMessages(chat.id);
  };

  const sendReply = () => {
    if (!replyText.trim() || !selectedChat) return;
    setSending(true);
    clearAlert();
    saveRecord(`/user/support/chats/${selectedChat.id}/reply`, { body: replyText })
      .then(() => { setReplyText(""); fetchMessages(selectedChat.id); })
      .catch((err) => setAlert("error", err.response.data.errors))
      .finally(() => setSending(false));
  };

  const closeChat = () => {
    if (!selectedChat) return;
    setClosing(true);
    saveRecord(`/user/support/chats/${selectedChat.id}/close`, {})
      .then(() => {
        setSelectedChat(null);
        setMessages([]);
        fetchChats();
        if (activeTab === 1) fetchArchive();
      })
      .catch((err) => setAlert("error", err.response.data.errors))
      .finally(() => setClosing(false));
  };

  const reopenChat = () => {
    if (!selectedChat) return;
    setReopening(true);
    saveRecord(`/user/support/chats/${selectedChat.id}/reopen`, {})
      .then(() => {
        setSelectedChat((prev) => ({ ...prev, status: "waiting" }));
        fetchChats();
        fetchArchive();
      })
      .catch((err) => setAlert("error", err.response.data.errors))
      .finally(() => setReopening(false));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  /*
  |--------------------------------------------------------------------------
  | Polling
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    fetchChats();
    pollChatsRef.current = setInterval(fetchChats, 10000);
    return () => clearInterval(pollChatsRef.current);
  }, []);

  useEffect(() => {
    if (activeTab === 1) fetchArchive();
  }, [activeTab]);

  useEffect(() => {
    if (!selectedChat || selectedChat.status === "closed") return;
    clearInterval(pollMsgsRef.current);
    pollMsgsRef.current = setInterval(() => fetchMessages(selectedChat.id), 5000);
    return () => clearInterval(pollMsgsRef.current);
  }, [selectedChat?.id]);

  const totalUnread = chats.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */
  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString("it-IT") : "";
  const isArchived  = selectedChat?.status === "closed";

  /*
  |--------------------------------------------------------------------------
  | Chat list item
  |--------------------------------------------------------------------------
  */
  const ChatItem = ({ chat }) => (
    <Box
      px="16px" py="14px" cursor="pointer"
      bg={selectedChat?.id === chat.id ? selectedBg : "transparent"}
      _hover={{ bg: selectedChat?.id === chat.id ? selectedBg : hoverBg }}
      borderBottom="1px solid" borderColor={borderClr}
      onClick={() => openChat(chat)}
    >
      <Flex align="center" gap="10px">
        <Avatar size="sm" name={chat.contact_name || chat.wa_phone} />
        <Box flex={1} minW={0}>
          <Flex align="center" justify="space-between">
            <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
              {chat.contact_name || chat.wa_phone}
            </Text>
            {chat.unread_count > 0 && (
              <Badge colorScheme="red" borderRadius="full" fontSize="xs" ml="6px">
                {chat.unread_count}
              </Badge>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.400" noOfLines={1} mt="2px">
            {chat.last_message || "Nessun messaggio"}
          </Text>
        </Box>
      </Flex>
      <Flex justify="space-between" mt="4px">
        <Badge
          colorScheme={chat.status === "waiting" ? "orange" : chat.status === "active" ? "green" : "gray"}
          fontSize="xs" variant="subtle"
        >
          {labelFor(supportChatStatusLabels, chat.status)}
        </Badge>
        <Text fontSize="xs" color="gray.400">{formatDate(chat.last_message_at || chat.created_at)}</Text>
      </Flex>
    </Box>
  );

  return (
    <Box pb="40px">
      <Flex align="center" mb="20px" gap="10px">
        <Text fontSize="2xl" fontWeight="bold">Assistenza</Text>
        {totalUnread > 0 && (
          <Badge colorScheme="red" borderRadius="full" px="8px" fontSize="sm">{totalUnread} da leggere</Badge>
        )}
        <Button size="sm" variant="ghost" leftIcon={<RefreshCw />} onClick={fetchChats} ml="auto">
          Aggiorna
        </Button>
      </Flex>

      <Flex gap="16px" h="calc(100vh - 200px)" minH="500px">

        {/* LEFT — Chat list with tabs */}
        <Box w={{ base: "100%", md: "300px" }} flexShrink={0} overflowY="auto">
          <Card p="0" h="100%" bg={cardBg} display="flex" flexDirection="column">
            <Tabs
              index={activeTab}
              onChange={(i) => { setActiveTab(i); setSelectedChat(null); setMessages([]); }}
              variant="unstyled" isFitted
            >
              <TabList p="12px" gap="8px" borderBottom="1px solid" borderColor={borderClr}>
                <Tab
                  py="9px" borderRadius="10px" fontWeight="semibold" fontSize="sm"
                  bg={activeTab === 0 ? "brand.500" : tabInactiveBg}
                  color={activeTab === 0 ? "white" : "gray.500"}
                  _hover={{ bg: activeTab === 0 ? "brand.400" : tabInactiveHover }}
                  _selected={{}} transition="all 0.2s"
                >
                  Aperte
                  {totalUnread > 0 && (
                    <Badge ml="7px" borderRadius="full" fontSize="xs" bg="red.500" color="white" px="6px">
                      {totalUnread}
                    </Badge>
                  )}
                </Tab>
                <Tab
                  py="9px" borderRadius="10px" fontWeight="semibold" fontSize="sm"
                  bg={activeTab === 1 ? "brand.500" : tabInactiveBg}
                  color={activeTab === 1 ? "white" : "gray.500"}
                  _hover={{ bg: activeTab === 1 ? "brand.400" : tabInactiveHover }}
                  _selected={{}} transition="all 0.2s"
                >
                  Archivio
                </Tab>
              </TabList>

              <TabPanels flex={1} overflowY="auto">

                {/* Open tab */}
                <TabPanel p="0">
                  {loadingChats ? (
                    <Flex justify="center" align="center" h="100px"><BeatLoader size={10} color="#422CFA" /></Flex>
                  ) : chats.length === 0 ? (
                    <Flex justify="center" align="center" h="100px">
                      <Text color="gray.400" fontSize="sm">Nessuna conversazione aperta</Text>
                    </Flex>
                  ) : chats.map((chat) => <ChatItem key={chat.id} chat={chat} />)}
                </TabPanel>

                {/* Archive tab */}
                <TabPanel p="0">
                  {loadingArchive ? (
                    <Flex justify="center" align="center" h="100px"><BeatLoader size={10} color="#422CFA" /></Flex>
                  ) : archive.length === 0 ? (
                    <Flex justify="center" align="center" h="100px">
                      <Text color="gray.400" fontSize="sm">Nessuna conversazione archiviata</Text>
                    </Flex>
                  ) : archive.map((chat) => <ChatItem key={chat.id} chat={chat} />)}
                </TabPanel>

              </TabPanels>
            </Tabs>
          </Card>
        </Box>

        {/* RIGHT — Chat window */}
        <Box flex={1} minW={0}>
          <Card p="0" h="100%" display="flex" flexDirection="column" bg={cardBg}>
            {!selectedChat ? (
              <Flex justify="center" align="center" flex={1}>
                <Text color="gray.400">Seleziona una conversazione per iniziare a chattare</Text>
              </Flex>
            ) : (
              <>
                {/* Header */}
                <Flex align="center" px="20px" py="14px" borderBottom="1px solid" borderColor={borderClr} gap="12px">
                  <Avatar size="sm" name={selectedChat.contact_name || selectedChat.wa_phone} />
                  <Box flex={1}>
                    <Text fontWeight="bold" fontSize="sm">{selectedChat.contact_name || selectedChat.wa_phone}</Text>
                    <Text fontSize="xs" color="gray.400">{selectedChat.wa_phone}</Text>
                  </Box>

                  {isArchived ? (
                    <Button
                      size="sm" colorScheme="green" variant="outline"
                      leftIcon={<ArchiveRestore />}
                      isLoading={reopening}
                      onClick={reopenChat}
                    >
                      Riapri
                    </Button>
                  ) : (
                    <Button
                      size="sm" colorScheme="red" variant="outline"
                      leftIcon={<X />}
                      isLoading={closing}
                      onClick={closeChat}
                    >
                      Chiudi Chat
                    </Button>
                  )}
                </Flex>

                <Box px="20px">{showAlert()}</Box>

                {/* Messages */}
                <Box flex={1} overflowY="auto" px="20px" py="16px">
                  {loadingMsgs ? (
                    <Flex justify="center" pt="40px"><BeatLoader size={10} color="#422CFA" /></Flex>
                  ) : messages.length === 0 ? (
                    <Flex justify="center" pt="40px">
                      <Text color="gray.400" fontSize="sm">Nessun messaggio</Text>
                    </Flex>
                  ) : messages.map((msg) => {
                    const isOut = msg.direction === "outbound";
                    return (
                      <Flex key={msg.id} justify={isOut ? "flex-end" : "flex-start"} mb="12px">
                        <Box maxW="70%">
                          <Box
                            bg={isOut ? "brand.500" : inboundBg}
                            color={isOut ? "white" : "inherit"}
                            px="14px" py="10px"
                            borderRadius={isOut ? "16px 16px 4px 16px" : "16px 16px 16px 4px"}
                            fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word"
                          >
                            {msg.body}
                          </Box>
                          <Flex justify={isOut ? "flex-end" : "flex-start"} mt="4px" gap="6px">
                            {isOut && msg.sent_by && <Text fontSize="xs" color="gray.400">{msg.sent_by} ·</Text>}
                            <Text fontSize="xs" color="gray.400">{formatTime(msg.created_at)}</Text>
                          </Flex>
                        </Box>
                      </Flex>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Reply box — only for open chats */}
                {!isArchived && (
                  <>
                    <Divider />
                    <Box px="20px" py="14px">
                      <Flex gap="10px" align="flex-end">
                        <Textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Scrivi un messaggio... (Invio per inviare, Maiusc+Invio per andare a capo)"
                          rows={2} resize="none" fontSize="sm" flex={1}
                        />
                        <Button
                          colorScheme="brand" leftIcon={sending ? null : <Send />}
                          onClick={sendReply} isDisabled={!replyText.trim() || sending} minW="90px"
                        >
                          {sending ? <Spinner size="sm" /> : "Invia"}
                        </Button>
                      </Flex>
                      <Text fontSize="xs" color="gray.400" mt="6px">Stai rispondendo come operatore via WhatsApp</Text>
                    </Box>
                  </>
                )}

                {isArchived && (
                  <Box px="20px" py="12px" borderTop="1px solid" borderColor={borderClr}>
                    <Text fontSize="xs" color="gray.400" textAlign="center">
                      Questa conversazione è archiviata. Clicca su Riapri per continuare a chattare.
                    </Text>
                  </Box>
                )}
              </>
            )}
          </Card>
        </Box>
      </Flex>
    </Box>
  );
}
